
import React, { useEffect, useState, useContext, SyntheticEvent } from 'react';
import styled from 'styled-components';

import { PageContext } from '../page';
import Dropdown from '../../../components/Dropdown';
import Input from '../../../components/forms/Input';
import Label from '../../../components/forms/Label';
import { getEmailParts } from './RegisteredEmailDomainInputContainer';

interface UsernamePlusDomainEmailInputProps {
  id: string;
  email: string;
  setEmail: (email: string) => void;
  domainList: string[];
  isUsernameError: boolean;
  isDomainError: boolean;
}

const StyledInput = styled(Input)`
  min-width: 120px;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 120px;
`;

const AtWrapper = styled.div`
  flex: 0 0 4%;
  margin: 30px 0px 0px 5px;
  font-size: 20px;
`;
const InlineWrapper = styled.div`
  flex: 0 0 40%;
`;

const IndentedInlineWrapper = styled.div`
  flex: 0 0 43%;
  margin-left: 5px;
`;

const UsernamePlusDomainEmailInput: React.FC<UsernamePlusDomainEmailInputProps> = ({
  id,
  email,
  setEmail,
  domainList,
  isUsernameError,
  isDomainError,
}) => {
  const { pushAlert } = useContext(PageContext);

  const [username, setUsername] = useState('');
  const [domain, setDomain] = useState('');
  const [dropdownPlaceholder, setDropdownPlaceholder] = useState<string | undefined>();

  const isDomainListEmpty = !domainList.length;

  useEffect(() => {
    const { username: initialUsername, domain: initialDomain } = getEmailParts(
      email,
    );
    const emailDomainInList = domainList.includes(initialDomain);
    setUsername(initialUsername);
    setDomain(emailDomainInList ? initialDomain : '');

    setDropdownPlaceholder(
      (isDomainError && (initialDomain || 'Please Select a Domain')) ||
        undefined,
    );
  }, [email, isDomainError, domainList]);

  const dropdownOptions = domainList.map(domainListItem => {
    return { value: domainListItem, text: domainListItem };
  });

  const inputOnChangeHandler = (event: SyntheticEvent<HTMLInputElement>) => {
    const usernameInputValue = event?.currentTarget?.value;
    setUsername(usernameInputValue);

    const candidateEmail = `${usernameInputValue}@${domain}`;
    // if invalid the form should be prevented from saving by parent components
    setEmail(candidateEmail);
  };

  const dropdownOnChangeHandler = (domainDropdownValue: string | null) => {
    if (domainDropdownValue && domainList?.includes(domainDropdownValue)) {
      setDomain(domainDropdownValue);
      setEmail(`${username}@${domainDropdownValue}`);
    } else {
      pushAlert('danger', 'Sorry, the selected email address is invalid.');
    }
  };

  const usernameInputId = `${id}-emailUsernameInput`;
  const domainInputId = `${id}-DomainInput`;

  return (
    <>
      <InlineWrapper>
        <Label htmlFor={usernameInputId} text="Username:" />
        <StyledInput
          id={usernameInputId}
          value={username}
          error={isUsernameError}
          onChange={inputOnChangeHandler}
          allowEmptyError={true}
          disabled={isDomainListEmpty}
        />
      </InlineWrapper>
      <AtWrapper>@</AtWrapper>
      <IndentedInlineWrapper>
        <Label htmlFor={domainInputId} text="Domain:" />
        <StyledDropdown
          id={domainInputId}
          placeholder={dropdownPlaceholder}
          value={domain}
          error={isDomainError}
          options={dropdownOptions}
          onChange={(value) => dropdownOnChangeHandler(value as string | null)}
          disabled={isDomainListEmpty}
        />
      </IndentedInlineWrapper>
    </>
  );
};

export default UsernamePlusDomainEmailInput;
